import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';

import { DATE_FILTERS } from '@shared/contants/variables';
import { TableComponent } from '@shared/components/table-component/table.component';
import { SalesPerformanceService } from '@services/sales-performance.service';
import { ISalesPerformanceResponse } from '@shared/interfaces/sales-performence.interface';
import { PageHeaderComponent } from '@shared/components/page-header/page-header.component';

import { ChartModule } from 'primeng/chart';
import { CardModule } from 'primeng/card';
import { IColumn } from '@shared/interfaces/column.interface';

@Component({
    selector: 'app-sales-report',
    imports: [CommonModule, ChartModule, TableComponent, CardModule, PageHeaderComponent],
    standalone: true,
    templateUrl: './sales-report.component.html',
    styleUrl: './sales-report.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SalesReportComponent implements OnInit {
    salesPerformanceService = inject(SalesPerformanceService);

    salesPerformanceData = signal<ISalesPerformanceResponse | null>(null);
    tableData = signal<any>([]);
    loading = signal<boolean>(false);
    chartData = signal<any>({
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [
            {
                label: 'Reservation',
                data: [],
                borderColor: '#8BC34A',
                backgroundColor: 'rgba(139, 195, 74, 0.2)',
                fill: true
            },
            {
                label: 'Seat',
                data: [],
                borderColor: '#1976D2',
                backgroundColor: 'rgba(25, 118, 210, 0.2)',
                fill: true
            },
            {
                label: 'Cancel',
                data: [],
                borderColor: '#D32F2F',
                backgroundColor: 'rgba(211, 47, 47, 0.2)',
                fill: true
            }
        ]
    });

    filterDateSearch = '';
    beginDate = '';
    endDate = '';
    cols: IColumn[] = [];
    chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
                align: 'end',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    font: { size: 12 }
                }
            }
        },
        scales: {
            x: { title: { display: true, text: 'Month', font: { size: 14, weight: 'bold' } } },
            y: { title: { display: true, text: 'Sales Number', font: { size: 14, weight: 'bold' } } }
        }
    };

    ngOnInit() {
        this.filterDateSearch = sessionStorage.getItem(DATE_FILTERS.FLIGHT_DATE_SEARCH) || '';
        this.beginDate = sessionStorage.getItem(DATE_FILTERS.BEGIN_DATE) || '';
        this.endDate = sessionStorage.getItem(DATE_FILTERS.END_DATE) || '';

        this.defineColumns();
        this.getSalesPerformance();
    }

    defineColumns() {
        this.cols = [
            { key: 'category', label: '' },
            { key: 'Jan', label: 'Jan' },
            { key: 'Feb', label: 'Feb' },
            { key: 'Mar', label: 'Mar' },
            { key: 'Apr', label: 'Apr' },
            { key: 'May', label: 'May' },
            { key: 'Jun', label: 'Jun' },
            { key: 'Jul', label: 'Jul' },
            { key: 'Aug', label: 'Aug' },
            { key: 'Sep', label: 'Sep' },
            { key: 'Oct', label: 'Oct' },
            { key: 'Nov', label: 'Nov' },
            { key: 'Dec', label: 'Dec' }
        ];
    }

    getSalesPerformance() {
        this.loading.set(true);
        this.salesPerformanceService.getSalesPerformance(this.filterDateSearch, this.beginDate, this.endDate).subscribe({
            next: (response: any) => {
                this.salesPerformanceData.set(response);
                this.editTableData(response);
                this.setChartData(response);
                this.loading.set(false);
            },
            error: (error) => {
                console.log(error);
                this.loading.set(false);
            }
        });
    }

    setChartData(response: any) {
        this.chartData.set({
            ...this.chartData(),
            datasets: this.chartData().datasets.map((dataset: any) => ({
                ...dataset,
                data: response.thisYear.monthly.map((monthData: any) => monthData[dataset.label.toLowerCase()])
            }))
        });
    }

    editTableData(response: any) {
        const categories = ['reservation', 'seat', 'cancel'];
        const tableData = categories.map((category) => ({
            category: category.charAt(0).toUpperCase() + category.slice(1),
            ...response.thisYear.monthly.reduce((acc: any, monthData: any, index: number) => {
                const monthName = new Date(0, index).toLocaleString('en', { month: 'short' });
                acc[monthName] = monthData[category];
                return acc;
            }, {})
        }));

        this.tableData.set(tableData);
    }

    comparisonClassControl(status?: string) {
        if (status === 'UP') {
            return 'comparison-up';
        } else if (status === 'DOWN') {
            return 'comparison-down';
        } else {
            return 'comparison-equal';
        }
    }
}
